import {
  AudioVideoObserver,
  ClientMetricReport,
  EventAttributes,
  EventName,
  EventObserver,
  MeetingSession,
  Versioning,
} from 'amazon-chime-sdk-js';

import {
  AvTelemetrySink,
  AvTelemetrySource,
} from 'src/app/features/video-conference/av-telemetry/models/av-telemetry-source';
import {
  AvTelemetryCadenceMode,
  AvTelemetryClientHints,
  AvTelemetryVendor,
} from 'src/app/features/video-conference/av-telemetry/models/av-telemetry.model';
import {
  CHIME_MEETING_HISTORY_WINDOW_MS,
  ChimeEventPayload,
  ChimeHistoryEntry,
  ChimeMetaPayload,
  ChimeSyntheticEventName,
  ChimeTickPayload,
} from 'src/app/features/video-conference/chime/models/av-telemetry.model';

/**
 * Chime adapter for the AV telemetry sampler.
 *
 * Chime is a `push` provider: `metricsDidReceive` fires on the SDK's own ~1 Hz cadence, so
 * this source never needs a timer. Ticks are forwarded raw — no downsampling, no renaming.
 *
 * Two distinct registration sites are required, which is easy to miss:
 *   - `metricsDidReceive` and `connectionDidBecomePoor/Good` are AudioVideoObserver callbacks.
 *   - `eventDidReceive` is an EventObserver on `meetingSession.eventController`.
 *
 * `eventController` is optional on MeetingSession, so its absence is tolerated: metrics
 * still flow, only the discrete-event stream is lost.
 *
 * Deliberately NOT constructed via DI — one instance per meeting session, created by the
 * component that owns the session.
 */
export class ChimeAvTelemetrySource implements AvTelemetrySource {
  readonly vendor: AvTelemetryVendor = 'chime';
  readonly vendorVersion: string = Versioning.sdkVersion;
  readonly cadenceMode: AvTelemetryCadenceMode = 'push';

  private sink: AvTelemetrySink | null = null;
  private audioVideoObserver: AudioVideoObserver | null = null;
  private eventObserver: EventObserver | null = null;

  constructor(
    private readonly meetingSession: MeetingSession,
    private readonly mediaRegion: string | null
  ) {}

  describeClient(): ChimeMetaPayload {
    const navigatorRef = typeof navigator === 'undefined' ? null : navigator;
    const screenRef = typeof screen === 'undefined' ? null : screen;

    return {
      meetingId: this.meetingId,
      attendeeId: this.attendeeId,
      mediaRegion: this.mediaRegion,
      userAgent: navigatorRef?.userAgent ?? '',
      clientHints: this.readLowEntropyClientHints(),
      language: navigatorRef?.language ?? '',
      hardwareConcurrency: navigatorRef?.hardwareConcurrency ?? null,
      // Chromium-only; absent elsewhere, which is itself a useful signal.
      deviceMemoryGb: (navigatorRef as { deviceMemory?: number })?.deviceMemory ?? null,
      screenWidth: screenRef?.width ?? null,
      screenHeight: screenRef?.height ?? null,
      devicePixelRatio: typeof window === 'undefined' ? null : (window.devicePixelRatio ?? null),
    };
  }

  attach(sink: AvTelemetrySink): void {
    this.sink = sink;

    this.audioVideoObserver = {
      metricsDidReceive: (clientMetricReport: ClientMetricReport) => {
        this.sink?.reportTick(this.toTickPayload(clientMetricReport));
      },
      connectionDidBecomePoor: () => {
        this.reportSyntheticEvent(ChimeSyntheticEventName.ConnectionDidBecomePoor);
      },
      connectionDidBecomeGood: () => {
        this.reportSyntheticEvent(ChimeSyntheticEventName.ConnectionDidBecomeGood);
      },
    };

    this.eventObserver = {
      eventDidReceive: (name: EventName, attributes: EventAttributes) => {
        this.sink?.reportEvent({
          meetingId: this.meetingId,
          name: name,
          attributes: this.withoutMeetingHistory(attributes),
          history: this.trimMeetingHistory(attributes),
        } satisfies ChimeEventPayload);
      },
    };

    this.meetingSession.audioVideo.addObserver(this.audioVideoObserver);
    this.meetingSession.eventController?.addObserver(this.eventObserver);

    // Metrics still flow without an eventController, but the discrete-event stream is gone.
    // Say so once, in-band: otherwise its absence reads downstream as "no disruptions".
    if (!this.meetingSession.eventController) {
      this.reportSyntheticEvent(ChimeSyntheticEventName.EventControllerUnavailable);
    }
  }

  detach(): void {
    if (this.audioVideoObserver) {
      this.meetingSession.audioVideo.removeObserver(this.audioVideoObserver);
      this.audioVideoObserver = null;
    }

    if (this.eventObserver) {
      this.meetingSession.eventController?.removeObserver(this.eventObserver);
      this.eventObserver = null;
    }

    this.sink = null;
  }

  private get meetingId(): string {
    return this.meetingSession.configuration?.meetingId ?? '';
  }

  private get attendeeId(): string {
    return this.meetingSession.configuration?.credentials?.attendeeId ?? '';
  }

  /**
   * Low-entropy hints only, read synchronously.
   *
   * `getHighEntropyValues()` returns a promise, and `describeClient()` is synchronous by
   * contract, so the high-entropy set is unavailable here even setting aside that telemetry
   * does not need it.
   */
  private readLowEntropyClientHints(): AvTelemetryClientHints {
    const uaData = typeof navigator === 'undefined' ? null : navigator.userAgentData;

    if (!uaData) {
      return { brands: null, mobile: null, platform: null };
    }

    return {
      brands: uaData.brands?.map((entry) => ({ brand: entry.brand, version: entry.version })) ?? null,
      mobile: uaData.mobile ?? null,
      platform: uaData.platform ?? null,
    };
  }

  private toTickPayload(clientMetricReport: ClientMetricReport): ChimeTickPayload {
    return {
      meetingId: this.meetingId,
      session: clientMetricReport.getObservableMetrics(),
      video: clientMetricReport.getObservableVideoMetrics(),
    };
  }

  private reportSyntheticEvent(name: ChimeSyntheticEventName): void {
    this.sink?.reportEvent({
      meetingId: this.meetingId,
      name: name,
      attributes: {},
    } satisfies ChimeEventPayload);
  }

  /**
   * `meetingHistory` is unbounded over a long session and would dominate every event
   * payload, so only the recent tail is kept, per AWS guidance.
   */
  private trimMeetingHistory(attributes: EventAttributes): ChimeHistoryEntry[] {
    const history = attributes?.meetingHistory;
    if (!history?.length) {
      return [];
    }

    const cutoffMs = history[history.length - 1].timestampMs - CHIME_MEETING_HISTORY_WINDOW_MS;

    return history
      .filter((entry) => entry.timestampMs >= cutoffMs)
      .map((entry) => ({ name: entry.name, timestampMs: entry.timestampMs }));
  }

  /** History travels in its own trimmed field, so it is not duplicated in attributes. */
  private withoutMeetingHistory(attributes: EventAttributes): Record<string, unknown> {
    const { meetingHistory, ...rest } = (attributes ?? {}) as EventAttributes &
      Record<string, unknown>;

    return rest;
  }
}
